import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import {
  useCreateCredential,
  useCredentials,
  useProviderSettings,
  useRotateCredential,
  useUpdateCredential,
  useUpsertProviderSetting,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { Credential, ProviderSetting } from '@/types/config';

import styles from './ConfigExternalApis.module.css';

interface ConfigField {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'password';
  hint?: string;
  placeholder?: string;
}

interface ExternalApiSpec {
  providerKey: string;
  label: string;
  description: string;
  secretLabel: string;
  configFields: ConfigField[];
  /**
   * Onda 4 (config por tenant): a integração é DO CLIENTE, não da casa. A
   * config vai para `api_credential.config_json` (que já tem dono e é o que o
   * resolvedor do backend lê) em vez de `provider_setting`, que é uma linha
   * global e só staff pode escrever. Sem isto, o admin do tenant tomava 404 ao
   * salvar — e a `base_url` que ele digitasse nunca chegaria ao adapter.
   */
  tenantScoped?: boolean;
}

// A API devolve valor sensível mascarado (`***` + 4 últimos) e reconhece a
// máscara de volta no PUT sem sobrescrever o segredo gravado.
const PREFIXO_MASCARA = '***';

// Exportado para o teste varrer a regra dos campos sensíveis (F-77).
export const EXTERNAL_APIS: ExternalApiSpec[] = [
  {
    providerKey: 'loterias_caixa',
    label: 'Loterias CAIXA',
    description:
      'Resultados oficiais de sorteios da Mega-Sena, Lotofácil, Quina. API direta + Scraper de fallback.',
    secretLabel: 'Chave / Token Privado (Opcional)',
    configFields: [
      { key: 'games', label: 'Jogos Ativos', placeholder: 'megasena, lotofacil, quina' },
      {
        key: 'frequency_seconds',
        label: 'Cadência de Checagem (seg)',
        type: 'number',
        placeholder: '300',
      },
      { key: 'fallback_enabled', label: 'Fallback Scraping (true/false)', placeholder: 'true' },
    ],
  },
  {
    providerKey: 'github_trending',
    label: 'GitHub Trending & Releases',
    description:
      'Monitoramento de lançamentos e releases em repositórios técnicos (vLLM, Ollama, LangChain).',
    secretLabel: 'GitHub Access Token (PAT)',
    configFields: [
      {
        key: 'repositories',
        label: 'Repositórios Monitorados',
        placeholder: 'ollama/ollama, vllm-project/vllm',
      },
      {
        key: 'frequency_seconds',
        label: 'Cadência de Checagem (seg)',
        type: 'number',
        placeholder: '600',
      },
    ],
  },
  {
    providerKey: 'serp',
    label: 'SERP API',
    description:
      'Google Trends, People-Also-Ask, busca orgânica. Usado em Trending Topics e SEO panel.',
    secretLabel: 'API key',
    configFields: [
      {
        key: 'provider_kind',
        label: 'Provider escolhido',
        type: 'text',
        hint: 'serpapi | searchapi  (define qual conector responde)',
        placeholder: 'serpapi',
      },
      { key: 'default_country', label: 'Default country', placeholder: 'br' },
      { key: 'default_language', label: 'Default language', placeholder: 'pt' },
      {
        key: 'default_location',
        label: 'Default location',
        placeholder: 'Brazil',
      },
      {
        key: 'cache_ttl_minutes',
        label: 'Cache TTL (min)',
        type: 'number',
        placeholder: '30',
      },
    ],
  },
  {
    providerKey: 'smtp',
    label: 'SMTP (notificações email)',
    description:
      'Servidor SMTP para o canal "email" das notificações. Recomendado: Amazon SES, SendGrid, Postmark. ' +
      '(o que você grava aqui ainda não é lido por ninguém: o envio de e-mail só usa as variáveis ' +
      'de ambiente SMTP_* do servidor — configurar por esta tela não dispara nada hoje)',
    secretLabel: 'Password',
    configFields: [
      {
        key: 'host',
        label: 'Host',
        placeholder: 'email-smtp.us-east-1.amazonaws.com',
      },
      { key: 'port', label: 'Port', type: 'number', placeholder: '587' },
      { key: 'user', label: 'User', placeholder: 'AKIA...' },
      {
        key: 'from_address',
        label: 'Remetente',
        placeholder: 'noreply@seudominio.com.br',
      },
      {
        key: 'use_tls',
        label: 'TLS (true/false)',
        type: 'text',
        placeholder: 'true',
      },
    ],
  },
  {
    providerKey: 'twitter',
    label: 'Twitter / X',
    // A descrição antiga ("Publicação automática de tweets a partir de stories")
    // era do fluxo do N8N, que saiu do produto. Nenhum código lê estas chaves:
    // core/providers.py:332 declara o provider e para aí; a publicação real no X
    // lê social_account.secret_enc (social_engine/engine.py:83), nunca o
    // provider_setting. E nada em services/ ou no scheduler publica em rede a
    // partir de story. O texto agora diz o que o card é de verdade.
    description:
      'Chaves legadas da API do X (v2). A publicação social HOJE é pelo motor de Redes Sociais (Contas) — este card fica para integrações antigas.',
    secretLabel: 'Bearer token',
    configFields: [
      // OAuth1 do X: os quatro valem tanto quanto o bearer. Sem type
      // 'password' o navegador os imprime em claro na tela (F-77).
      { key: 'api_key', label: 'API key', type: 'password' },
      { key: 'api_secret', label: 'API secret', type: 'password' },
      { key: 'access_token', label: 'Access token', type: 'password' },
      {
        key: 'access_token_secret',
        label: 'Access token secret',
        type: 'password',
      },
      { key: 'username', label: 'Username', placeholder: '@seuperfil' },
    ],
  },
  {
    providerKey: 'freeastrology',
    label: 'FreeAstrology API',
    description:
      'API de horóscopos diários. Usado pelo Horóscopo Worker. Migrar do nó hardcoded no N8N.',
    secretLabel: 'API key',
    configFields: [
      { key: 'default_lang', label: 'Default language', placeholder: 'pt' },
      {
        key: 'timezone',
        label: 'Timezone',
        placeholder: 'America/Sao_Paulo',
      },
    ],
  },
  {
    providerKey: 'psi',
    label: 'PageSpeed Insights (Core Web Vitals)',
    description:
      'Mede LCP/CLS/INP das matérias (aba Otimização → "Medir CWV"). Chave grátis do Google Cloud, 25k/dia. Sem chave dá 429.',
    secretLabel: 'API key',
    configFields: [],
  },
  {
    providerKey: 'ion_hub',
    label: 'ION HUB (MOS / TV)',
    tenantScoped: true,
    description:
      'Recebe roteiros de TV (laudas MOS) via ION HUB e cria pautas pra adaptar TV→web. O worker puxa as laudas READY_TO_AIR a cada 60s.',
    secretLabel: 'Token (opcional)',
    configFields: [
      {
        key: 'base_url',
        label: 'URL do ION HUB',
        placeholder: 'http://ion-hub:8080',
        hint: 'Onde a API/dashboard do ION HUB responde (porta 8080).',
      },
    ],
  },
];

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export function ConfigExternalApis() {
  const { data: settings = [] } = useProviderSettings();
  const { data: credentials = [] } = useCredentials();
  const createCred = useCreateCredential();
  const updateCred = useUpdateCredential();
  const rotateCred = useRotateCredential();
  const upsertSetting = useUpsertProviderSetting();

  const settingByProvider = useMemo(
    () => Object.fromEntries(settings.map((s: ProviderSetting) => [s.provider, s])),
    [settings],
  );
  const credByProvider = useMemo(
    () => Object.fromEntries(credentials.map((c: Credential) => [c.provider, c])),
    [credentials],
  );

  const [editing, setEditing] = useState<ExternalApiSpec | null>(null);
  const [secret, setSecret] = useState('');
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  function openEdit(spec: ExternalApiSpec) {
    const cfg = (
      spec.tenantScoped
        ? (credByProvider[spec.providerKey]?.config ?? {})
        : (settingByProvider[spec.providerKey]?.config ?? {})
    ) as Record<string, unknown>;
    const draft: Record<string, string> = {};
    for (const field of spec.configFields) {
      const value = cfg[field.key];
      draft[field.key] = value === undefined || value === null ? '' : String(value);
    }
    setEditing(spec);
    setConfigForm(draft);
    setSecret('');
    setMsg(null);
  }

  function closeEdit() {
    setEditing(null);
    setConfigForm({});
    setSecret('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);

    // Coerce numeric fields to numbers where it makes sense
    const configPayload: Record<string, unknown> = {};
    for (const field of editing.configFields) {
      const raw = configForm[field.key]?.trim();
      if (!raw) continue;
      if (field.type === 'number') {
        const n = Number(raw);
        if (Number.isFinite(n)) configPayload[field.key] = n;
      } else {
        configPayload[field.key] = raw;
      }
    }

    try {
      const existingCred = credByProvider[editing.providerKey];

      if (!editing.tenantScoped) {
        await upsertSetting.mutateAsync({
          provider: editing.providerKey,
          config: configPayload,
        });
      } else if (existingCred) {
        // Integração do tenant: a config mora na própria credencial dele.
        await updateCred.mutateAsync({
          id: existingCred.id,
          payload: { config: configPayload },
        });
      }

      if (secret) {
        if (existingCred) {
          await rotateCred.mutateAsync({
            id: existingCred.id,
            payload: { secret, reason: 'updated via external-apis page' },
          });
        } else {
          await createCred.mutateAsync({
            provider: editing.providerKey,
            slug: 'main',
            secret,
            config: configPayload,
            notes: `created via external-apis page`,
          });
        }
      } else if (editing.tenantScoped && !existingCred) {
        // Sem token (o ION HUB de referência não exige auth) a linha ainda
        // precisa existir: é ela que carrega a base_url E o dono.
        await createCred.mutateAsync({
          provider: editing.providerKey,
          slug: 'main',
          config: configPayload,
          notes: `created via external-apis page`,
        });
      }

      setMsg(`${editing.label} atualizado.`);
      closeEdit();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>APIs externas</h1>
        <p className={styles.subtitle}>
          SERP, SMTP, Twitter/X, FreeAstrology e outros serviços de terceiros. As chaves ficam
          Fernet-encrypted. As configs da casa vão para
          <code> provider_setting</code>; as da integração do seu cliente (ION HUB) ficam na própria
          credencial, que já tem dono.
        </p>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      <ul className={styles.list}>
        {EXTERNAL_APIS.map((spec) => {
          const cred = credByProvider[spec.providerKey] as Credential | undefined;
          const settingRow = settingByProvider[spec.providerKey] as ProviderSetting | undefined;
          // Integração do tenant: a config exibida é a da credencial dele.
          const cfg = (spec.tenantScoped ? cred?.config : settingRow?.config) as
            | Record<string, unknown>
            | undefined;
          const setting = spec.tenantScoped
            ? cfg && Object.keys(cfg).length > 0
              ? { config: cfg }
              : undefined
            : settingRow;
          return (
            <li key={spec.providerKey} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{spec.label}</span>
                {cred?.has_secret ? (
                  <Badge tone="ok" size="sm">
                    chave configurada
                  </Badge>
                ) : (
                  <Badge tone="warn" size="sm">
                    sem chave
                  </Badge>
                )}
                {setting && (
                  <Badge tone="info" size="sm">
                    config: {Object.keys(setting.config).length} keys
                  </Badge>
                )}
              </div>
              <p className={styles.desc}>{spec.description}</p>

              {cred && (
                <div className={styles.row}>
                  <span className={styles.label}>{spec.secretLabel}</span>
                  <span className={styles.mono}>{cred.secret_masked}</span>
                </div>
              )}

              {setting && Object.keys(setting.config).length > 0 && (
                <div className={styles.cfgGrid}>
                  {Object.entries(setting.config).map(([k, v]) => (
                    <div key={k} className={styles.cfgRow}>
                      <span className={styles.cfgKey}>{k}</span>
                      <span className={styles.cfgValue}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                <Button size="sm" variant="secondary" onClick={() => openEdit(spec)}>
                  Configurar
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h2 className={styles.modalTitle}>{editing.label}</h2>
            <p className={styles.modalSub}>{editing.description}</p>

            <label className={styles.field}>
              <span>
                {editing.secretLabel}
                {credByProvider[editing.providerKey] ? ' (deixar vazio mantém o atual)' : ''}
              </span>
              <input
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
              />
            </label>

            {editing.configFields.map((field) => (
              <label key={field.key} className={styles.field}>
                <span>{field.label}</span>
                <input
                  type={field.type ?? 'text'}
                  value={configForm[field.key] ?? ''}
                  onChange={(e) =>
                    setConfigForm({
                      ...configForm,
                      [field.key]: e.target.value,
                    })
                  }
                  placeholder={field.placeholder}
                />
                {configForm[field.key]?.startsWith(PREFIXO_MASCARA) && (
                  <span className={styles.hint}>
                    Valor guardado. Deixe como está para manter; digite por cima para trocar.
                  </span>
                )}
                {field.hint && <span className={styles.hint}>{field.hint}</span>}
              </label>
            ))}

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={
                  upsertSetting.isPending ||
                  createCred.isPending ||
                  updateCred.isPending ||
                  rotateCred.isPending
                }
              >
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
